import React, { useState, useEffect } from "react";
import { useNotify } from "react-admin";
import { Button, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { StateBadge, ImpactText } from "../badges";
import { fieldImpactLabel } from "../labels";
import { BUCKET_OF } from "../methodologyUtils";

// Shared gap-resolution logic + row UI for the entity fichas (material / supplier / packaging).
// Entity gaps are deduped by field (an item's `gapIds` are ALL its underlying gap rows — e.g. a
// field missing on two legs). So:
//   - assume / request / reject → PATCH /gaps/bulk with gapIds (resolves every leg at once).
//   - provide (carries a value; bulk has no resolvedValue) → PATCH /gaps/:id per id.
// The backend auto-triggers the completeness job on resolve, so callers pass onChanged to refresh
// derived scores. Rows update OPTIMISTICALLY (no per-entity gaps refetch endpoint exists).

export const isPending = (status) => ["faltan", "revisar", "asumibles"].includes(BUCKET_OF[status] || "faltan");

export const sortPendingFirst = (gaps) =>
  [...gaps].sort((a, b) => (isPending(b.status) ? 1 : 0) - (isPending(a.status) ? 1 : 0));

export const pendingCountOf = (gaps) => gaps.filter((g) => isPending(g.status)).length;

const idsOf = (gap) => (gap.gapIds && gap.gapIds.length ? gap.gapIds : [gap.id]);

export const useGapResolution = (api, initialGaps, resetKey, onChanged) => {
  const notify = useNotify();
  const [rows, setRows] = useState(initialGaps || []);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setRows(initialGaps || []);
  }, [resetKey]); // eslint-disable-line

  const apply = async (gap, doIt, okMsg, optimistic, requireProcessed) => {
    setBusyId(gap.id);
    const res = await doIt();
    setBusyId(null);
    const applied = res.ok && (!requireProcessed || (res.processed ?? 1) > 0);
    if (applied) {
      notify(okMsg, "success");
      setRows((rs) => rs.map((x) => (x.id === gap.id ? { ...x, ...optimistic } : x)));
      if (onChanged) onChanged();
    } else if (res.ok) {
      notify(`No se pudo aplicar "${gap.displayName}": sin valor por defecto aplicable`, "warning");
    } else {
      notify(`Error ${res.status || ""} al resolver`.trim(), "error");
    }
  };

  const bulk = async (gap, action, extra) => {
    const r = await api("PATCH", "/gaps/bulk", { gapIds: idsOf(gap), action, ...extra });
    return { ok: r.ok, status: r.status, processed: r.data?.processed };
  };

  const assume = (gap) =>
    apply(gap, () => bulk(gap, "RESOLVED_ASSUMPTION", { resolvedByActor: "BCOME" }), "Valor asumido aplicado", { status: "RESOLVED_ASSUMPTION" }, true);

  const reject = (gap) => {
    if (!window.confirm("¿Rechazar la assumption? El gap volverá a contar como falta hasta que llegue el dato real.")) return;
    apply(gap, () => bulk(gap, "ASSUMPTION_REJECTED", { resolvedByActor: "BRAND" }), "Assumption rechazada", { status: "ASSUMPTION_REJECTED", resolvedValue: null });
  };

  const provide = (gap, value) => {
    const v = (value || "").trim();
    if (!v) return notify("Introduce un valor antes de aportar", "warning");
    apply(
      gap,
      async () => {
        let ok = true;
        let status = 0;
        for (const id of idsOf(gap)) {
          const r = await api("PATCH", `/gaps/${id}`, { status: "RESOLVED_PROVIDED", resolvedByActor: "BRAND", resolvedValue: v });
          if (!r.ok) {
            ok = false;
            status = r.status;
          }
        }
        return { ok, status };
      },
      "Dato aportado",
      { status: "RESOLVED_PROVIDED", resolvedValue: v },
    );
  };

  return { rows, busyId, provide, assume, reject };
};

const useRowStyles = makeStyles((theme) => ({
  row: { display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${theme.palette.divider}`, fontSize: 13, flexWrap: "wrap" },
  statusCol: { width: 150, flexShrink: 0 },
  grow: { flex: "1 1 auto", minWidth: 0 },
  value: { color: theme.palette.text.secondary, fontSize: 12 },
  actions: { display: "flex", gap: 4, alignItems: "center", flexShrink: 0, flexWrap: "wrap" },
  // Compact buttons, at the scale of the row text (not the default chunky MUI small button).
  btn: { padding: "0 6px", fontSize: 11, minWidth: 0, lineHeight: 1.8, minHeight: 24 },
  provInput: { width: 72 },
}));

// One gap row with inline actions (Asumir / Aportar / Rechazar). No "marcar como pedido".
export const GapActionRow = ({ gap, contracted, busy, onProvide, onAssume, onReject }) => {
  const classes = useRowStyles();
  const [val, setVal] = useState("");
  const assumed = gap.status === "RESOLVED_ASSUMPTION";
  const provided = gap.status === "RESOLVED_PROVIDED";
  const assumable = gap.status === "ASSUMPTION_PENDING";
  const impact = fieldImpactLabel(gap, { allowed: contracted });
  return (
    <div className={classes.row}>
      <span className={classes.statusCol}>
        <StateBadge status={gap.status} />
      </span>
      <span className={classes.grow} title={gap.fieldKey}>
        {gap.displayName}
        {(assumed || provided) && gap.resolvedValue && <span className={classes.value}> · {gap.resolvedValue}</span>}
      </span>
      {impact ? <ImpactText label={impact} blocking={!assumed && !provided} /> : null}
      <span className={classes.actions}>
        {assumed ? (
          <Button size="small" variant="outlined" className={classes.btn} disabled={busy} onClick={() => onReject(gap)}>
            Rechazar
          </Button>
        ) : !provided ? (
          <>
            {assumable && (
              <Button size="small" variant="contained" color="primary" className={classes.btn} disabled={busy} onClick={() => onAssume(gap)}>
                Asumir
              </Button>
            )}
            <TextField className={classes.provInput} size="small" placeholder="valor" value={val} onChange={(e) => setVal(e.target.value)} />
            <Button size="small" variant={assumable ? "outlined" : "contained"} color="primary" className={classes.btn} disabled={busy} onClick={() => onProvide(gap, val)}>
              Aportar
            </Button>
          </>
        ) : null}
      </span>
    </div>
  );
};
